// hooks/useCategorys.js
import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    getUserNotifications,
    sendUserNotifications,
    statistics,
    updateUserNotifications,
    updateAlerts,
    getAlerts,
    analyticsTimeSeries,
} from "services/home";

const useHome = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch Categorys
    const fetchData = useCallback(async (cityId) => {
        setLoading(true);
        try {
            const data = await statistics(cityId);
            return data;
        } catch (err) {
            setError(err.message || "Failed to fetch Categorys");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAlerts = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getAlerts();
            return data;
        } catch (err) {
            setError(err.message || "Failed to fetch Categorys");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchNotification = useCallback(async (params) => {
        setLoading(true);
        try {
            const data = await getUserNotifications(params);
            return data;
        } catch (err) {
            setError(err.message || "Failed to fetch notification");
        } finally {
            setLoading(false);
        }
    }, []);

    const updateAlertsContent = useCallback(async (newData) => {
        setLoading(true);
        try {
            const data = await updateAlerts(newData);
            return data;
        } catch (err) {
            setError(err.message || "Failed to edit notification");
            // Return the error, like useOrder#editOrder: the callers pass the
            // result to readApiError. Returning nothing made every failure
            // read as a success (admin#70).
            return err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateNotification = useCallback(async () => {
        setLoading(true);
        try {
            const data = await updateUserNotifications();
            return data;
        } catch (err) {
            setError(err.message || "Failed to edit notification");
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * RETHROWS. It used to swallow the failure and return `undefined`, so every
     * caller's `catch` was dead code and both send screens showed
     * «تم ارسال اشعار بنجاح» on a request that had failed. `setError` is kept
     * for anything reading the hook's error state.
     */
    const sendNotification = useCallback(async (data) => {
        setLoading(true);
        try {
            return await sendUserNotifications(data);
        } catch (err) {
            setError(err.message || "Failed to send notification");
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        loading,
        error,
        fetchNotification,
        sendNotification,
        updateNotification,
        updateAlertsContent,
        fetchData,
        fetchAlerts,
    };
};

export default useHome;

// --- The home page statistics, on TanStack Query ---

export const homeKeys = {
    all: ["home-statistics"],
    byCity: (cityId) => [...homeKeys.all, { cityId: cityId ?? null }],
};

export const analyticsKeys = {
    all: ["dashboard-analytics"],
    timeSeries: (cityId, params) => [...analyticsKeys.all, { cityId: cityId ?? null, params }],
};

/** Fetches the dashboard time series for the selected city and range. */
export const useGetAnalyticsTimeSeries = (cityId, filters = {}) => {
    const { enabled = true, ...params } = filters;
    const query = useQuery({
        queryKey: analyticsKeys.timeSeries(cityId, params),
        queryFn: () =>
            analyticsTimeSeries({
                ...params,
                cityId: cityId || undefined,
            }),
        enabled,
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        staleTime: 30_000,
    });

    return {
        data: query.data,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};

/**
 * The stat cards and the monthly chart.
 *
 * WHY THIS IS POLLED. The page fetched once, on mount and on a city change,
 * and then never again. Half of what it shows moves while you watch it: the
 * driver count is derived from `users.online` and a live app ping, and the
 * order figures change with every checkout. A dashboard whose numbers were true
 * when the tab was opened this morning is worse than one that says it is
 * loading — nothing on screen said the figures had aged.
 *
 * 30 seconds, the same interval the account lists use. The driver card is
 * measured against a 60-second "open" threshold on the server, so a slower poll
 * would let the page contradict its own tooltip.
 *
 * `staleTime` of 15s is the small cache: navigating to a list and straight back
 * repaints from cache instead of re-hitting the API, while anything older than
 * a quarter of the poll refetches on sight.
 *
 * NO `placeholderData`. It would carry the previous CITY's figures into the new
 * city's cards while the request was in flight — the same defect that made the
 * people pages show each other's accounts. Numbers that belong to the wrong
 * place are worse than a spinner, because nothing about them looks wrong.
 */
export const useGetStatistics = (cityId, options = {}) => {
    const query = useQuery({
        queryKey: homeKeys.byCity(cityId),
        queryFn: () => statistics(cityId),
        enabled: options.enabled ?? true,
        refetchInterval: 30_000,
        // Coming back to the tab is the moment an operator most wants the truth.
        refetchOnWindowFocus: true,
        staleTime: 15_000,
    });

    return {
        data: query.data,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};
